// Ticket 26: Gift History Tab Component
import React, { useState, useEffect } from 'react';
import { fetchReceivedGifts } from '../api/wallet';
import { MapPin, Crown } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { normalizeList } from '../utils/normalize';

const GiftHistoryTab = () => {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadGifts = async () => {
      try {
        setLoading(true);
        const data = await fetchReceivedGifts();
        setGifts(normalizeList(data, ['gifts']));
      } catch (err) {
        console.error('[GiftHistory] Failed to fetch gifts:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadGifts();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-lg text-gray-600">Loading gift history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-2">Failed to load gift history</div>
        <div className="text-sm text-gray-500">{error}</div>
      </div>
    );
  }

  if (gifts.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🎁</div>
        <div className="text-xl font-playfair text-gray-700 mb-2">No gifts yet</div>
        <div className="text-gray-500">When someone sends you a gift, it will appear here</div>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {gifts.map((gift) => {
        const senderName = gift.sender_profile?.names?.[0] || 'Someone';
        const location = gift.sender_profile?.location;
        const age = gift.sender_profile?.age;
        const isElite = gift.sender_profile?.is_elite;
        
        // Format timestamp
        let timeAgo = '';
        try {
          const createdAt = new Date(gift.created_at);
          timeAgo = formatDistanceToNow(createdAt, { addSuffix: true });
        } catch (e) {
          timeAgo = 'recently';
        }

        return (
          <div
            key={gift.id}
            className="glass rounded-2xl p-6 card-hover"
          >
            {/* Gift Display */}
            <div className="h-32 bg-gradient-rose-gold rounded-xl mb-4 flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl mb-2">{gift.gift_emoji}</div>
                <div className="text-white font-medium">{gift.gift_name}</div>
              </div>
            </div>

            {/* Sender Info */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-gradient-rose-gold rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">{senderName.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-gray-900">{senderName}</span>
                      {isElite && <Crown className="w-3 h-3 text-amber-500" />}
                    </div>
                    {age && (
                      <div className="text-xs text-gray-500">
                        {age} years old
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {location?.city && (
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>{location.city}</span>
                </div>
              )}

              <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                Sent {timeAgo}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GiftHistoryTab;
